import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResourceSerializer } from "./rest-resource.interface";
import { GenericAPIResource, GenericListApiResource } from "./generic-api.model";
import { Observable } from 'rxjs';
import { map, mergeMap, concatMapTo, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RestResourceService {


  constructor(
    private httpClient: HttpClient,
    private url: string,
    private serializer: ResourceSerializer
  ) {

  }

  private cleanUrl(url: string): string {
    return url.slice(-1) == '/' ? url.slice(0, -1) : url
  }
  public create<T extends GenericAPIResource>(endpoint: string, item: T): Observable<T> {
    return this.httpClient
      .post<T>(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}`, this.serializer.toJson(item))
      .pipe(map(data => this.convertData<T>(data)));
  }
  public createVoid<T extends GenericAPIResource>(endpoint: string, item: T): Observable<Object> {
    return this.httpClient
      .post<boolean>(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}`, this.serializer.toJson(item));

  }
  public update<T extends GenericAPIResource>(endpoint: string, item: T): Observable<T> {
    return this.httpClient
      .post<T>(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}`,
        this.serializer.toJson(item))
      .pipe(map(data => this.serializer.fromJson(data) as T));
  }
  public updateVoid<T extends GenericAPIResource>(endpoint: string, item: T): Observable<Object> {
    return this.httpClient
      .post<boolean>(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}`, this.serializer.toJson(item));
  }
  public GetOne<T extends GenericAPIResource>(endpoint: string, id: string): Observable<T> {
    return this.httpClient
      .get(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}/${id}`)
      .pipe(map((data: any) => this.convertData<T>(data)));
  }
  public GetObject(endpoint: string, params: string[]): Observable<Object> {
    let urlParams: string = "";
    if (params.length > 0) {
      urlParams = params.join('/');
    }
    return this.httpClient.get(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}/${urlParams}`);
  }

  public Get<T extends GenericAPIResource>(endpoint: string, queryOptions: string): Observable<T[]> {
    return this.httpClient.get(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}?${queryOptions}`).pipe(map((data: any) => this.convertData<T>(data)));
  }
  public GetMVCRoute<T extends GenericAPIResource>(endpoint: string, param: string[]): Observable<T[]> {
    let urlParams: string = "";
    if (param && param.length > 0) {
      urlParams = param.join('/');
    }
    return this.httpClient.get(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}/${urlParams}`).pipe(map((data: any) => this.convertData<T>(data)));
  }

  public GetOneMVCRoute<T extends GenericAPIResource>(endpoint: string, param: string[]): Observable<T> {
    let urlParams: string = "";
    if (param && param.length > 0) {
      urlParams = param.join('/');
    }
    return this.httpClient.get(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}/${urlParams}`).pipe(map((data: any) => this.convertData<T>(data)));
  }

  public GetWithPost<T extends GenericAPIResource>(endpoint: string, formData: any): Observable<T[]> {
    return this.httpClient.post<T>(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}`,
      formData).pipe(map((data: any) => this.convertData<T>(data)));
  }

  public SetWithPost<T extends GenericAPIResource>(endpoint: string, formData: any): Observable<T> {
    return this.httpClient.post<T>(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}`,
      formData).pipe(map((data: any) => this.convertData<T>(data)));
  }


  public Delete(endpoint: string, id: number): Observable<Object> {
    return this.httpClient.delete(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}/${id}`);
  }
  public DeleteWithPost(endpoint: string, id: number): Observable<Object> {
    return this.httpClient.post<boolean>(`${this.cleanUrl(this.url)}/${this.cleanUrl(endpoint)}`, id);
  }

  private convertData<T extends GenericAPIResource>(data: any): any {
    if (data.hasOwnProperty('list'))
      return this.convertList<T>(data)

    if (data.hasOwnProperty('element'))
      return this.convertGeneric<T>(data.element);
    return this.convertGeneric<T>(data);
  }

  private convertGeneric<T extends GenericAPIResource>(data: any): T {
    return this.serializer.fromJson(data);
  }

  private convertList<T extends GenericAPIResource>(data: any): T[] {
    return data.list.map(item => this.serializer.fromJson(item));
  }

}
